import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import components
import AdminDashboard from './components/AdminDashboard';
import ApiTester from './components/ApiTester';
import LoginForm from './components/LoginForm';
import SignupPage from './components/SignupPage';
import Reports from './components/Reports';
import AddOnServicesPage from './components/AddOnServicesPage';

// Import services
import { authService } from './services/api';

// Import error handling components and hooks
import { OfflineBanner, ConnectionStatus } from './components/ui/ErrorComponents';
import SystemStatusGate from './components/SystemStatusGate';
import { useNetworkStatus, useIsMobile } from './hooks';
import { networkMonitor } from './utils/errorHandler';

// Import RBAC utilities
import { ROLES, PERMISSIONS, hasPermission, canAccessAdminDashboard, getRoleInfo } from './utils/rbac';
import { Ban as BanIcon, Settings as SettingsIcon, LogOut as LogOutIcon } from 'lucide-react';
import dridhLogo from './assets/dridh-logo.png';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('login'); // Start with login for admin access
  const { isOnline } = useNetworkStatus();
  const isMobile = useIsMobile();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Re-check auth when coming back online
  useEffect(() => {
    if (isOnline && user === null && !loading) {
      checkAuthStatus();
    }
  }, [isOnline]);

  const checkAuthStatus = async () => {
    // Don't try to check auth if offline
    if (!navigator.onLine) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (token) {
        const userData = await authService.getCurrentUser();
        // Fix: Handle the response data structure correctly
        const user = userData.data?.user || userData.user || userData;
        setUser(user);
        
        // Check if user can access admin dashboard
        if (canAccessAdminDashboard(user?.role)) {
          setCurrentView('admin');
        } else {
          // Users without admin access should use the booking engine
          setCurrentView('login');
          const roleInfo = getRoleInfo(user?.role);
          toast.info(`${roleInfo.label} - Please use the booking engine for guest bookings`);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      
      // Handle network errors gracefully
      if (error.isNetworkError || error.isTimeoutError) {
        toast.error('Unable to verify login. Please check your connection.', {
          autoClose: 5000
        });
      } else {
        localStorage.removeItem('token');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (userData, token) => {
    try {
      // Store the token
      localStorage.setItem('token', token);
      
      // Set the user state
      setUser(userData);
      
      // Route based on user role - check if user can access admin dashboard
      if (canAccessAdminDashboard(userData.role)) {
        setCurrentView('admin');
        const roleInfo = getRoleInfo(userData.role);
        toast.success(`Welcome to the Admin Dashboard, ${userData.name || 'User'}!`);
      } else {
        // Users without admin access should be redirected to booking engine
        setCurrentView('login');
        const roleInfo = getRoleInfo(userData.role);
        toast.warning(`${roleInfo.label} - Admin access required. Please use the booking engine for guest bookings.`);
        handleLogout(); // Log them out
      }
    } catch (error) {
      console.error('Login handler error:', error);
    }
  };

  const handleSignup = async (userData, token) => {
    try {
      // Store the token
      localStorage.setItem('token', token);
      
      // Set the user state
      setUser(userData);
      
      // Check if user can access admin dashboard
      if (canAccessAdminDashboard(userData.role)) {
        setCurrentView('admin');
        const roleInfo = getRoleInfo(userData.role);
        toast.success(`Account created successfully! Welcome to the Admin Dashboard.`);
      } else {
        setCurrentView('login');
        const roleInfo = getRoleInfo(userData.role);
        toast.warning(`${roleInfo.label} - This is the admin panel. Please use the booking engine for guest accounts.`);
        handleLogout(); // Log them out
      }
    } catch (error) {
      console.error('Signup handler error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setCurrentView('login');
      toast.info('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      localStorage.removeItem('token');
      setUser(null);
      setCurrentView('login');
    }
  };

  // Profile edits (name, phone, email) show up in the dashboard header without a reload
  const handleUserUpdated = (changes) => {
    setUser((current) => (current ? { ...current, ...changes } : current));
  };

  const showLogin = () => {
    setCurrentView('login');
  };

  const showAdmin = () => {
    // Check if user can access admin dashboard
    if (user && canAccessAdminDashboard(user.role)) {
      setCurrentView('admin');
    } else {
      const roleInfo = getRoleInfo(user?.role);
      toast.error(`Admin access required`);
    }
  };

  const showSignup = () => {
    setCurrentView('signup');
  };

  const showApiTester = () => {
    setCurrentView('api-tester');
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#F5F3EB'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px' }}><SettingsIcon size="1em" aria-hidden="true" /></div>
          <div style={{ fontSize: '18px', color: '#5F6B61' }}>Loading Admin Dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    // Replaces the dashboard with the maintenance screen whenever a super admin
    // has paused the PMS. Super admins pass through and get a banner instead.
    <SystemStatusGate user={user} onLogout={handleLogout}>
    <Router>
      <div className="App">
        {/* Offline Banner - shows when user loses connection */}
        <OfflineBanner 
          isVisible={!isOnline} 
          onRetry={() => window.location.reload()} 
        />
        
        {/* Admin Header Navigation - Desktop only (Mobile nav is handled inside AdminDashboard) */}
        {!isMobile && (
        <nav className="brand-header" style={{
          marginTop: !isOnline ? '48px' : '0', // Add margin when offline banner is visible
          transition: 'margin-top 0.3s ease'
        }}>
          <div className="brand-header__identity">
            {/* Connection Status Indicator */}
            <ConnectionStatus isOnline={isOnline} showWhenOnline={false} />

            <img src={dridhLogo} alt="Dridh" className="brand-header__logo" />
            <span className="brand-header__divider" aria-hidden="true" />
            <span className="brand-header__title">Admin Panel</span>

            {user && canAccessAdminDashboard(user.role) && (
              <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                <button
                  onClick={showAdmin}
                  className={`brand-tab ${currentView === 'admin' ? 'brand-tab--active' : ''}`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setCurrentView('reports')}
                  className={`brand-tab ${currentView === 'reports' ? 'brand-tab--active' : ''}`}
                >
                  Reports
                </button>
                <button
                  onClick={() => setCurrentView('addon-services')}
                  className={`brand-tab ${currentView === 'addon-services' ? 'brand-tab--active' : ''}`}
                >
                  Services
                </button>
                <button
                  onClick={showApiTester}
                  className={`brand-tab ${currentView === 'api-tester' ? 'brand-tab--active' : ''}`}
                >
                  API Tester
                </button>
              </div>
            )}
          </div>

          <div className="brand-header__actions">
            {user ? (
              <>
                <span style={{ fontWeight: '600', color: '#36433B' }}>
                  {user.name || user.email} ({getRoleInfo(user.role).label})
                </span>
                <button onClick={handleLogout} className="brand-header-btn brand-header-btn--danger">
                  <LogOutIcon size={16} aria-hidden="true" />
                  Logout
                </button>
              </>
            ) : (
              <button onClick={showLogin} className="brand-header-btn brand-header-btn--primary">
                Admin Login
              </button>
            )}

            {/* Link to Booking Engine */}
            <a
              href="http://localhost:3000"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-header-btn"
            >
              Booking Engine
              <span style={{ fontSize: '12px', opacity: 0.8 }}>↗</span>
            </a>
          </div>
        </nav>
        )} {/* end !isMobile desktop navigation */}

        {/* Mobile brand bar for logged-out screens (logged-in mobile nav lives in AdminDashboard) */}
        {isMobile && !user && (
          <div className="brand-header" style={{ padding: '10px 16px' }}>
            <img src={dridhLogo} alt="Dridh" className="brand-header__logo" style={{ height: '32px' }} />
          </div>
        )}

        {/* Main Content */}
        <div style={{ 
          minHeight: isMobile ? '100vh' : 'calc(100vh - 80px)', 
          backgroundColor: '#F5F3EB' 
        }}>
          {!user && currentView === 'login' && (
            <div style={{ padding: isMobile ? '20px' : '40px 20px' }}>
              <div style={{ 
                maxWidth: '400px', 
                margin: '0 auto',
                textAlign: 'center',
                marginBottom: isMobile ? '20px' : '32px'
              }}>
                <h1 style={{ 
                  fontSize: isMobile ? '24px' : '32px', 
                  fontWeight: '800', 
                  color: '#24332A',
                  marginBottom: '16px'
                }}>
                  Admin Panel Access
                </h1>
                <p style={{ 
                  color: '#5F6B61', 
                  fontSize: '16px',
                  marginBottom: '24px'
                }}>
                  This is the administrative interface for Dave PMS. Only staff members, property admins, and super admins can access this panel.
                </p>
                <div style={{
                  backgroundColor: '#F3E6CB',
                  border: '1px solid #B68235',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px'
                }}>
                  <p style={{ 
                    color: '#684A1F', 
                    fontSize: '14px',
                    margin: '0',
                    fontWeight: '600'
                  }}>
                    Looking to make a booking? Use the Booking Engine instead!
                  </p>
                </div>
              </div>
              <LoginForm onLogin={handleLogin} onShowSignup={showSignup} />
            </div>
          )}
          
          {!user && currentView === 'signup' && (
            <div style={{ padding: '40px 20px' }}>
              <div style={{ 
                maxWidth: '400px', 
                margin: '0 auto',
                textAlign: 'center',
                marginBottom: '32px'
              }}>
                <h1 style={{ 
                  fontSize: '32px', 
                  fontWeight: '800', 
                  color: '#24332A',
                  marginBottom: '16px'
                }}>
                  Admin Account Registration
                </h1>
                <p style={{ 
                  color: '#5F6B61', 
                  fontSize: '16px',
                  marginBottom: '24px'
                }}>
                  Create an admin account to access the management panel.
                </p>
              </div>
              <SignupPage onSignup={handleSignup} onBack={showLogin} />
            </div>
          )}
          
          {user && canAccessAdminDashboard(user.role) && currentView === 'admin' && (
            <AdminDashboard user={user} onUserUpdated={handleUserUpdated} />
          )}
          
          {user && hasPermission(user.role, PERMISSIONS.REPORT_READ) && currentView === 'reports' && (
            <div style={{ padding: '20px' }}>
              <Reports user={user} />
            </div>
          )}
          
          {user && hasPermission(user.role, PERMISSIONS.ADDON_READ) && currentView === 'addon-services' && (
            <AddOnServicesPage user={user} />
          )}
          
          {user && currentView === 'api-tester' && (
            <div style={{ padding: '20px' }}>
              <ApiTester />
            </div>
          )}

          {/* Access Denied for non-admin users */}
          {user && !canAccessAdminDashboard(user.role) && (
            <div style={{ 
              padding: '60px 20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '24px' }}><BanIcon size="1em" aria-hidden="true" /></div>
              <h1 style={{ 
                fontSize: '32px', 
                fontWeight: '800', 
                color: '#8E3628',
                marginBottom: '16px'
              }}>
                Access Denied
              </h1>
              <p style={{ 
                color: '#5F6B61', 
                fontSize: '18px',
                marginBottom: '32px'
              }}>
                You don't have permission to access the admin panel.
              </p>
              <a
                href="http://localhost:3000"
                style={{
                  backgroundColor: '#1A3E2A',
                  color: 'white',
                  textDecoration: 'none',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Go to Booking Engine
              </a>
            </div>
          )}
        </div>

        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </Router>
    </SystemStatusGate>
  );
}

export default App;
